/* global google */

import A1GA from './A1GA';
import { optionalCookiesAllowed } from './optionalCookies';

// @TODO move all the ad stuff into its own module/class?
// @TODO check how to load another ad after a certain amount of time, for example when switching streams
// See here how to query for another ad: https://developers.google.com/interactive-media-ads/docs/sdks/html5/client-side/faq#8
// @TODO check if we should request another ad after a certain amount of time or when switching streams

const AD_MEDIA_LOAD_TIMEOUT = 15000;
const AD_READY_POLL_INTERVAL = 250;
const AD_READY_WAIT_TIMEOUT = AD_MEDIA_LOAD_TIMEOUT + 3000;
const AUDIO_AD_SLOT_SIZE = 1;

const HIDDEN_MEDIA_STYLE = {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    pointerEvents: 'none',
};

class AudioEngine {
    _debuggingEnabled = false;

    _userIdOrBrowserHash = null; // If given, use the userId, otherwise use the browserHash. It is used in the TargetSpot ad request

    _isPro = null;

    _timeWaited = 0; // Time we are currently waiting for the ad stuff to load

    _maxWaitTime = AD_READY_WAIT_TIMEOUT; // Maximum time we will wait for the ad stuff to load before cancelling and playing the stream

    _adStuffLoadingEventCallBack = () => this.debug('adStuffLoading event callback not registered'); // We use this to set isLoading to true while loading the ad stuff

    _requestedPlayerState = null; // This is mostly to not start an ad if the cancelled the loading while we still have been initializing all the ad stuff

    _skipNextPrerollAd = false;

    // They will be set by the server via the database
    _adTagUrls = {
        prerollAdTagUrlAdManager: null, // This is a standard Ad Manager VAST adTag url
        prerollAdTagUrlTargetSpot: null, // This is a TargetSpot VAST adTag url and will have some of the parameters replaced on the client (<userid>, <useragent>, ...). See the _getTargetSpotAdResponse() function
    };

    _adTagUsed = null;

    _adResponse = null;

    _adsRequested = false;

    _adsRequestInProgress = null;

    _targetSpotFallbackAttempted = false;

    _adDisplayContainerInitialized = false;

    _createAudio = () => {
        this.audio = document.createElement('audio');

        this.audio.preload = 'none';

        this.audio.setAttribute('playsinline', '');

        this.audio.setAttribute('webkit-playsinline', '');
    };

    _initIfNeeded = () => {
        if (!this.audio) {
            this._createAudio();

            this.unlockQueue = [];

            this.adUnlockQueue = [];

            this._iosEnable();
        }
    };

    initAds = async () => {
        this.debug('initAds');

        if (!optionalCookiesAllowed()) return false;

        if (!this.audio) {
            // eslint-disable-next-line no-console
            console.error('[initAds] audio not yet created, aborting adInit');

            return false;
        }

        if (this._isPro) {
            this.debug('[initAds] user is PRO, aborting adInit');

            return false;
        }

        if (!this._adTagUrls.prerollAdTagUrlAdManager && !this._adTagUrls.prerollAdTagUrlTargetSpot) {
            this.debug('[initAds] no adTagUrls, aborting adInit');

            return false;
        }

        this._loadImaSdk();

        return true;
    }

    _activate = () => {
        this.debug('_activate');

        this._restoreEventListeners();

        this._runUnlockQueue();

        if (this._oldSrc) {
            this._oldSrc = this._oldSrc.split('?FYC')[0]; // remove old FYC parameters (if present)

            this.audio.src = `${this._oldSrc}?FYC=${(new Date()).getTime()}`; // (Fuck You Cache -> to fuck the cache in firefox (and probably others))

            if (this._oldVolume) {
                this.audio.volume = this._oldVolume;
            }

            this.audio.load();

            this._oldSrc = null;

            this._oldVolume = null;
        }

        this.canPlayEvent = typeof this.audio.oncanplaythrough === 'undefined' ? 'canplay' : 'canplaythrough';

        if (this._initListener) {
            this._initListener(this);
        }

        this.debug('AudioEngine initialized! canPlayEvent: ', this.canPlayEvent);
    };

    _unlock = () => {
        this.debug('_unlock');

        if (!this.audio) {
            this._createAudio();
        }

        this.audio.src = '/silent.wav';

        this.audio.load();

        this.unlocked = true;

        document.removeEventListener('touchstart', this._unlock, true);

        document.removeEventListener('touchend', this._unlock, true);

        document.removeEventListener('click', this._unlock, true);

        this._activate();
    };

    _iosEnable = () => {
        this.debug('_iosEnable');

        if ('ontouchstart' in window && !this.unlocked) {
            this.debug('[_iosEnable] ontouchstart detected in window');

            this.debug('[_iosEnable] unlock listeners registered');

            document.addEventListener('touchstart', this._unlock, true);

            document.addEventListener('touchend', this._unlock, true);

            // on some very few browsers this returns true, without ever firing any touch event :( (check https://stackoverflow.com/questions/46917481/ontouchstart-exists-in-window-object-on-non-touch-devices)
            // so we add a normal click event listener as well that will also unlock the player, if it is fired. (This should not cause problems on mobile devices)
            document.addEventListener('click', this._unlock, true);
        } else {
            this.debug('[_iosEnable] no need for unlock listeners or already unlocked');

            this.unlocked = true;

            this._activate();
        }
    };

    _clearUnlockQueue = () => {
        this.unlockQueue = [];
    };

    _enqueuePostUnlockFunction = (func, args = []) => {
        this.unlockQueue.push({
            func,
            args,
        });
    };

    _enqueuePostAdsManagerLoadedFunction = (func, args = []) => {
        this.adUnlockQueue.push({
            func,
            args,
        });
    };

    _runUnlockQueue = () => {
        if (this.unlockQueue && this.unlockQueue.length) {
            this.debug('running unlock queue funcs');

            this.unlockQueue.forEach((u) => u.func(...u.args));

            this._clearUnlockQueue();
        }
    };

    _runAdUnlockQueue = () => {
        if (this.adUnlockQueue && this.adUnlockQueue.length) {
            this.debug('running ad unlock queue funcs');

            this.adUnlockQueue.forEach((u) => u.func(...u.args));

            this.adUnlockQueue = [];
        }
    };

    _mountAdPlaybackElements = () => {
        if (typeof document === 'undefined' || !document.body) {
            return;
        }

        if (this.audio && !this.audio.parentNode) {
            Object.assign(this.audio.style, HIDDEN_MEDIA_STYLE);

            document.body.appendChild(this.audio);
        }

        if (this.adContainer) {
            Object.assign(this.adContainer.style, HIDDEN_MEDIA_STYLE);

            this.adContainer.setAttribute('aria-hidden', 'true');
        }
    };

    _resumeContentAfterAdError = () => {
        if (!this.audio) {
            // the audio element may have been torn down in the meantime (e.g. by an unrelated error-recovery cycle)
            return;
        }

        if (this._srcBeforeAd && this._srcBeforeAd !== this.audio.src) {
            // This happens on iOS devices. The old src is not restored. No idea why :/

            // Furthermore, on iOS the pause event is emitted shortly before the ad stops.
            // Thats why we start the player again from outside the AudioEngine, so that we can properly set the shouldBePlaying property

            this.audio.src = this._srcBeforeAd;

            this.audio.load();
        } else {
            // In all other cases, we can and have to start the player again right here
            this.audio.play().catch((error) => this.debug('[_resumeContentAfterAdError] audio.play() rejected: ', error));
        }

        // Not sure if this is necessary or makes any sense, but it is like this in their docs
        if (this.adsManager) {
            this.adsManager.destroy();

            this.adsManager = null;
        }
    };

    _pickSource = (sources) => {
        if (typeof (sources) === 'string') {
            return sources;
        }

        // search for 'probably' playable source
        for (let i = 0; i < sources.length; i += 1) {
            if (this.audio.canPlayType(sources[i].type) === 'probably') {
                return sources[i].src;
            }
        }

        // if no 'probably' playable source was found, check for 'maybe' source
        for (let i = 0; i < sources.length; i += 1) {
            if (this.audio.canPlayType(sources[i].type) === 'maybe') {
                return sources[i].src;
            }
        }

        // if there is no 'maybe' playable source neither, well, shit :(
        // eslint-disable-next-line no-console
        console.error('No compatible source found!');

        return null;
    };


    _buildAdManagerAdTagUrl = () => {
        this.debug('_buildAdManagerAdTagUrl');

        // IMA test ad tag urls
        // const adTagUrl = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480&cust_params=sample_ct%3Dlinear&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';
        // const adTagUrl = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';

        // Ad Manager Ad Tag Url (dev)
        // const adManagerAdTagUrl = 'https://pubads.g.doubleclick.net/gampad/live/ads?iu=/60229764/a1nova_audio-test-ad&description_url=https%3A%2F%2Fa1dev1.antena1.com.br&tfcd=0&npa=0&ad_type=audio&sz=1x1&min_ad_duration=5000&max_ad_duration=20000&gdfp_req=1&output=vast&unviewed_position_start=1&env=instream&vpmute=0&impl=s&correlator=';
        // (prod)
        // const adManagerAdTagUrl = 'https://pubads.g.doubleclick.net/gampad/live/ads?iu=/60229764/a1nova_preroll-audio&description_url=https%3A%2F%2Fwww.antena1.com.br&tfcd=0&npa=0&ad_type=audio&sz=1x1&min_ad_duration=5000&max_ad_duration=30000&gdfp_req=1&output=vast&unviewed_position_start=1&env=instream&vpmute=0&impl=s&correlator=';
        const rawAdManagerAdTagUrl = this._adTagUrls.prerollAdTagUrlAdManager;

        if (!rawAdManagerAdTagUrl) {
            this.debug('[_buildAdManagerAdTagUrl] no AdManager adTagUrl');

            return false;
        }

        // Inject required stream parameters for Ad Manager.
        const url = (window && window.location && window.location.href) || 'https://www.antena1.com.br';
        const correlator = Date.now(); // Must be unique per session
        // sid=0 -> Opting out of passing the sid (session id parameter) as we are passing the ppid parameter instead
        // pod=1 -> Pod number (we do not really have pods, as far as I understand, but it appears to be necessary for frequency capping)
        // ppos=1 -> Position in pod (we do not really have pods, as far as I understand, but it appears to be necessary for frequency capping)
        // scor=1 -> stream correlator (this should be unique for each stream. It appears to be necessary for frequency capping) @TODO use the streamId here, so that different streams might play different ads
        // hl=pt -> language (hardcoding to portuguese as we are currently only using the adManager to traffic our own ads) @TODO use the browsers language if we want to enable programtic ads one day
        const adManagerAdTagUrl = new URL(rawAdManagerAdTagUrl);
        adManagerAdTagUrl.searchParams.set('ad_type', 'audio');
        adManagerAdTagUrl.searchParams.set('description_url', url);
        adManagerAdTagUrl.searchParams.set('env', 'instream');
        adManagerAdTagUrl.searchParams.set('vpmute', '0');
        adManagerAdTagUrl.searchParams.set('correlator', correlator);
        adManagerAdTagUrl.searchParams.set('url', url);
        adManagerAdTagUrl.searchParams.set('pod', '1');
        adManagerAdTagUrl.searchParams.set('ppos', '1');
        adManagerAdTagUrl.searchParams.set('scor', correlator);
        adManagerAdTagUrl.searchParams.set('hl', 'pt');
        adManagerAdTagUrl.searchParams.set('sid', '0');
        adManagerAdTagUrl.searchParams.set('sz', `${AUDIO_AD_SLOT_SIZE}x${AUDIO_AD_SLOT_SIZE}`);

        if (this._userIdOrBrowserHash) {
            adManagerAdTagUrl.searchParams.set('ppid', this._userIdOrBrowserHash);
        }

        this.debug('[_buildAdManagerAdTagUrl] adManagerAdTagUrl', adManagerAdTagUrl.toString());
        return adManagerAdTagUrl.toString();
    }

    /**
     * Fetches a TargetSpot VAST response.
     *
     * We still fetch TargetSpot manually because its tag URL needs client side parameters
     * such as domain, user agent, IP and user id.
     *
     * @returns string | false
     */
    _getTargetSpotAdResponse = async () => {
        this.debug('_getTargetSpotAdResponse');

        try {
            const rawTargetSpotAdTagUrl = this._adTagUrls.prerollAdTagUrlTargetSpot;

            if (!rawTargetSpotAdTagUrl) {
                this.debug('[_getTargetSpotAdResponse] no TargetSpot adTagUrl');

                return false;
            }

            const hostName = (window && window.location && window.location.hostname) || 'antena1.com.br';
            const origin = (window && window.location && window.location.origin) || 'antena1.com.br';
            const userAgent = (navigator && navigator.userAgent) || 'no useragent';

            // No ad returned, fetch the users IP
            const userInfosUrl = hostName.indexOf('localhost') === -1 ? `https://${hostName}/getUserInfos` : 'https://a1dev1.antena1.com.br/getUserInfos'; // use a1dev1 on localhost because it has the correct CORS headers
            const userInfos = await fetch(userInfosUrl).then((response) => response.json());
            let userIP = '';

            if (userInfos?.status === 'success') {
                // Extract the users IP (forwardedFor contains all proxy IPs as well, including Sucuri)
                const proxyIPs = userInfos?.data?.forwardedFor.split(', ');

                userIP = proxyIPs[0];
            }

            this.debug('[_getTargetSpotAdResponse] userIP', userIP);

            this.debug('[_getTargetSpotAdResponse] userIdOrBrowserHash', this._userIdOrBrowserHash);

            // Construct the targetspot adTag url
            // dev
            // https://api.tsbluebox.com/163/vast/3.0/?apikey=7a001b7c-1b0a-451b-944e-79c3ee55de9d&stationid=staging-RadioAntena1-WebPlayer&externalid=staging-RadioAntena1-WebPlayer&product=radiostreaming&placement=preroll&transcoding=1&audioformat=audio/mpeg&bitrate=128&contentformat-iabv2=680&domain=<domain>&useragent=<useragent>&ip=<ip>&referr=<referr>&userid=<userid>
            // Example:
            /* const targetSpotAdTagUrl = 'https://api.tsbluebox.com/163/vast/3.0/?apikey=7a001b7c-1b0a-451b-944e-79c3ee55de9d&stationid=staging-RadioAntena1-WebPlayer&externalid=staging-RadioAntena1-WebPlayer&product=radiostreaming&placement=preroll'
            + '&transcoding=1'
            + '&audioformat=audio/mpeg'
            + '&bitrate=128'
            + '&contentformat-iabv2=680'
            + `&domain=${encodeURIComponent(hostName)}`
            + `&useragent=${encodeURIComponent(userAgent)}`
            + `&ip=${encodeURIComponent(userIP)}`
            + `&referr=${encodeURIComponent(origin)}`
            + `&userid=${encodeURIComponent(this._userIdOrBrowserHash)}`;
            // &gdpr=1 // @TODO think about those params
            // &gdpr_consent=CPBIiguPBIiguAHABBENBLCgAP_AAE7AAAAAAAAAAAAA.f_gACdgAAAAA */

            const targetSpotAdTagUrl = rawTargetSpotAdTagUrl
                .replace('<domain>', encodeURIComponent(hostName))
                .replace('<useragent>', encodeURIComponent(userAgent))
                .replace('<ip>', encodeURIComponent(userIP))
                .replace('<referr>', encodeURIComponent(origin))
                .replace('<userid>', encodeURIComponent(this._userIdOrBrowserHash));

            this.debug('[_getTargetSpotAdResponse] targetSpotAdTagUrl', targetSpotAdTagUrl);

            // Fetch the TargetSpot VAST response
            const targetSpotResponse = await fetch(targetSpotAdTagUrl).then((response) => response.text());

            this.debug('[_getTargetSpotAdResponse] targetSpotResponse', targetSpotResponse);

            if (targetSpotResponse.indexOf('<Ad id=') > -1) {
                return targetSpotResponse;
            }

            this.debug('[_getTargetSpotAdResponse] no targetSpot ad');

            return false;
        } catch (error) {
            // Something went wrong
            // eslint-disable-next-line no-console
            console.warn('error fetching VAST', error);

            // Log the error
            A1GA.event({
                category: 'PrerollAudioAd',
                action: '[TargetSpot] getAdTagUrlError',
                label: `Error: ${error}`,
                nonInteraction: false, // Not sure if this is correct...
            });

            return false;
        }
    }

    _requestTargetSpotAds = async () => {
        this.debug('_requestTargetSpotAds');

        if (this._adsRequested) {
            return true;
        }

        if (this._adsRequestInProgress) {
            return this._adsRequestInProgress;
        }

        if (!this.adsLoader) {
            throw new Error('AdsLoader not ready');
        }

        this._adsRequestInProgress = (async () => {
            const adsRequest = new google.ima.AdsRequest();

            this._configureAudioAdsRequest(adsRequest);

            this._adTagUsed = 'TargetSpot';
            this._adResponse = await this._getTargetSpotAdResponse();

            if (!this._adResponse) {
                throw new Error('No ad');
            }

            adsRequest.adsResponse = this._adResponse;
            adsRequest.setAdWillAutoPlay(false); // This tells the SDK that we will start the ad by our selfs. Not sure if it makes any difference in our case

            this._adsRequested = true;

            try {
                this.adsLoader.requestAds(adsRequest);
            } catch (error) {
                this._adsRequested = false;

                throw error;
            }

            return true;
        })();

        try {
            return await this._adsRequestInProgress;
        } finally {
            this._adsRequestInProgress = null;
        }
    }

    _requestAds = async () => {
        this.debug('_requestAds');

        if (this._adsRequested) {
            return true;
        }

        if (this._adsRequestInProgress) {
            return this._adsRequestInProgress;
        }

        if (!this.adsLoader) {
            throw new Error('AdsLoader not ready');
        }

        const adManagerAdTagUrl = this._buildAdManagerAdTagUrl();

        if (!adManagerAdTagUrl) {
            return this._requestTargetSpotAds();
        }

        this._adsRequestInProgress = (async () => {
            const adsRequest = new google.ima.AdsRequest();

            this._configureAudioAdsRequest(adsRequest);

            this._adTagUsed = 'AdManager';
            this._adResponse = null;
            this._targetSpotFallbackAttempted = false;

            adsRequest.adTagUrl = adManagerAdTagUrl;
            adsRequest.setAdWillAutoPlay(false); // This tells the SDK that we will start the ad by our selfs. Not sure if it makes any difference in our case

            this._adsRequested = true;

            try {
                this.adsLoader.requestAds(adsRequest);
            } catch (error) {
                this._adsRequested = false;

                throw error;
            }

            return true;
        })();

        try {
            return await this._adsRequestInProgress;
        } finally {
            this._adsRequestInProgress = null;
        }
    }

    _configureAudioAdsRequest = (adsRequest) => {
        /* eslint-disable no-param-reassign */

        adsRequest.linearAdSlotWidth = AUDIO_AD_SLOT_SIZE;
        adsRequest.linearAdSlotHeight = AUDIO_AD_SLOT_SIZE;
        adsRequest.nonLinearAdSlotWidth = AUDIO_AD_SLOT_SIZE;
        adsRequest.nonLinearAdSlotHeight = AUDIO_AD_SLOT_SIZE;

        if (adsRequest.setAdWillPlayMuted) {
            adsRequest.setAdWillPlayMuted(false);
        }
    }

    _onLoadAppendImaSdk = async () => {
        this.debug('_onLoadAppendImaSdk');

        // Loaded the IMA Sdk

        // Create the ads container
        this.adContainer = document.createElement('div');

        this.adContainer.id = 'ad-container';

        this._mountAdPlaybackElements();

        // Inject the div into the dom, or the AdsManager will complain
        document.body.appendChild(this.adContainer);

        // Load the AdsManager
        this.adDisplayContainer = new google.ima.AdDisplayContainer(this.adContainer, this.audio);

        this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);

        this.adsLoader.addEventListener(
            google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
            (adsManagerLoadedEvent) => {
                this.debug('[_onLoadAppendImaSdk] [adsLoader] adsmanager loaded');

                const adsRenderingSettings = new google.ima.AdsRenderingSettings();

                adsRenderingSettings.loadVideoTimeout = AD_MEDIA_LOAD_TIMEOUT;
                adsRenderingSettings.enablePreloading = true;
                adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

                // Instantiate the AdsManager from the adsLoader response and pass it the ad audio element
                this.adsManager = adsManagerLoadedEvent.getAdsManager(
                    this.audio,
                    adsRenderingSettings,
                );

                this.adsManager.addEventListener(
                    google.ima.AdErrorEvent.Type.AD_ERROR,
                    (adErrorEvent) => {
                        const adError = adErrorEvent.getError();

                        this.debug('[_onLoadAppendImaSdk] [adsManager] ad error', adError);

                        // Log the error
                        A1GA.event({
                            category: 'PrerollAudioAd',
                            action: `[${this._adTagUsed}] adsManagerError`,
                            label: `Error Code: ${adError.getErrorCode()}`,
                            nonInteraction: false, // Not sure if this is correct...
                        });

                        this._isAdPlaying = false;

                        this._adLoaded = false;

                        this._adError = true;

                        this._adsRequested = false;

                        this._resumeContentAfterAdError();
                    },
                );

                this.adsManager.addEventListener(
                    google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
                    () => {
                        this.debug('[_onLoadAppendImaSdk] [adsManager] content pause requested');

                        this._isAdPlaying = true;

                        this._adLoaded = true;
                    },
                );

                this.adsManager.addEventListener(
                    google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
                    () => {
                        this.debug('[_onLoadAppendImaSdk] [adsManager] content resume requested');

                        this._isAdPlaying = false;

                        this._adLoaded = false;

                        this._adPlayed = true;

                        if (!this.audio) {
                            // the audio element may have been torn down in the meantime (e.g. by an unrelated error-recovery cycle)
                            return;
                        }

                        if (this._srcBeforeAd && this._srcBeforeAd !== this.audio.src) {
                            // This happens on iOS devices. The old src is not restored. No idea why :/

                            // Furthermore, on iOS the pause event is emitted shortly before the ad stops.
                            // Thats why we start the player again from outside the AudioEngine, so that we can properly set the shouldBePlaying property

                            this.audio.src = this._srcBeforeAd;

                            this.audio.load();
                        } else {
                            // In all other cases, we can and have to start the player again right here
                            this.audio.play().catch((error) => this.debug('[CONTENT_RESUME_REQUESTED] audio.play() rejected: ', error));
                        }
                    },
                );

                [
                    google.ima.AdEvent.Type.LOADED,
                    google.ima.AdEvent.Type.STARTED,
                    google.ima.AdEvent.Type.COMPLETE,
                    google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
                ].forEach((eventType) => {
                    this.adsManager.addEventListener(eventType, (adEvent) => {
                        this.debug('[_onLoadAppendImaSdk] [adsManager] ad event', adEvent.type);
                    });
                });

                // console.log('ima types', google.ima.AdEvent.Type);

                this._runAdUnlockQueue();
            },
            false,
        );

        this.adsLoader.addEventListener(
            google.ima.AdErrorEvent.Type.AD_ERROR,
            (adErrorEvent) => {
                const adError = adErrorEvent.getError();

                // Handle the error logging.
                this.debug('[_onLoadAppendImaSdk] [adsLoader] ad error', adError);

                // Log the error
                A1GA.event({
                    category: 'PrerollAudioAd',
                    action: `[${this._adTagUsed}] adsLoaderError`,
                    label: `Error Code: ${adError.getErrorCode()}`,
                    nonInteraction: false, // Not sure if this is correct...
                });

                if (this._adTagUsed === 'AdManager' && this._adTagUrls.prerollAdTagUrlTargetSpot && !this._targetSpotFallbackAttempted) {
                    this.debug('[_onLoadAppendImaSdk] [adsLoader] trying TargetSpot fallback');

                    this._targetSpotFallbackAttempted = true;

                    this._adsRequested = false;

                    this._adsRequestInProgress = null;

                    this._adError = false;

                    this._requestTargetSpotAds()
                        .catch((targetSpotError) => {
                            this.debug('[_onLoadAppendImaSdk] [adsLoader] targetSpot fallback error', targetSpotError);

                            A1GA.event({
                                category: 'PrerollAudioAd',
                                action: '[TargetSpot] adsLoaderFallbackError',
                                label: `Error: ${targetSpotError}`,
                                nonInteraction: false, // Not sure if this is correct...
                            });

                            this._adLoaded = false;

                            this._adError = true;

                            this._adsRequested = false;

                            this._resumeContentAfterAdError();
                        });

                    return;
                }

                this._adLoaded = false;

                this._adError = true;

                this._adsRequested = false;

                this._resumeContentAfterAdError();
            },
            false,
        );

        return true;
    };

    // Log an error if we are unable to retrieve IMA Sdk
    _onLoadErrorImaSdk = (error) => {
        // eslint-disable-next-line no-console
        console.error('Could not load IMA Sdk.', error);
    };

    // Loads the IMA Sdk
    _loadImaSdk = () => {
        if (!this.imaSdkScriptInjected) {
            this.debug('_loadImaSdk');

            const head = document.head || document.getElementsByTagName('head')[0];

            // Create our script tag with async set to true and append it to our head
            const rmpScript = document.createElement('script');

            rmpScript.src = this._debuggingEnabled ? '//imasdk.googleapis.com/js/sdkloader/ima3_debug.js' : '//imasdk.googleapis.com/js/sdkloader/ima3.js';

            rmpScript.async = true;

            rmpScript.addEventListener('load', this._onLoadAppendImaSdk);

            rmpScript.addEventListener('error', this._onLoadErrorImaSdk);

            head.appendChild(rmpScript);

            this.imaSdkScriptInjected = true;
        }
    }

    _delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

    /**
     * Try to load an ad
     *
     * It will wait several seconds for all of the ad stuff to load before aborting
     *
     * @returns Promise
     */
    _loadAds = async () => {
        this.debug('_loadAds');

        if (!optionalCookiesAllowed()) throw new Error('Optional advertising cookies declined');

        this._timeWaited = 0;

        if (this._isPro) {
            // Do not play an ad for this user
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('User is PRO');
        }

        if (!this._adTagUrls.prerollAdTagUrlAdManager && !this._adTagUrls.prerollAdTagUrlTargetSpot) {
            // No adTagUrls from the database
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('No adTagUrls');
        }

        if (!this.imaSdkScriptInjected && !this.adDisplayContainer) {
            // The ad setup was not initialized yet. This can happen if a logged in user clicks play
            // before the profile request finishes and tells us whether the user is PRO.
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('Ads not initialized');
        }

        // Set isLoading to true on the front
        this._adStuffLoadingEventCallBack();

        if (!this.adDisplayContainer) {
            // The IMA Sdk did not yet initialize
            // Wait for a maximum time of _maxWaitTime before aborting
            this.debug('[_loadAds] Waiting for adDisplayContainer...');

            while (!this.adDisplayContainer && this._timeWaited < this._maxWaitTime) {
                // eslint-disable-next-line no-await-in-loop
                await this._delay(AD_READY_POLL_INTERVAL);

                this._timeWaited += AD_READY_POLL_INTERVAL;
            }

            this.debug(`[_loadAds] Waited ${this._timeWaited}ms`);

            // Log it
            A1GA.event({
                category: 'PrerollAudioAd',
                action: `[${this._adTagUsed}] loadAdsError`,
                label: `Waited (adDisplayContainer) (ms): ${this._timeWaited} - loaded: ${!!this.adDisplayContainer}`,
                nonInteraction: false, // Not sure if this is correct...
            });

            // if the adDisplayContainer still did not load, abort
            if (!this.adDisplayContainer) {
                // eslint-disable-next-line prefer-promise-reject-errors
                return Promise.reject('adDisplayContainer not loaded');
            }
        }

        if (this._adPlayed) {
            // We have already played the ad
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('Already played ad');
        }

        if (this._adError) {
            // The last ad had an error, do not try to play it again.
            // Note: In a next version of this code we could try to fetch another ad in this case...
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('Last Ad had an error');
        }

        if (this._adLoaded) {
            // Resume the playback
            this.adsManager.resume();

            return Promise.resolve('Resuming ad');
        }

        this._srcBeforeAd = this.audio.src;

        if (!this.audio.paused) {
            this.audio.pause();
        }

        if (!this._adDisplayContainerInitialized) {
            this.adDisplayContainer.initialize();

            this._adDisplayContainerInitialized = true;
        }

        if (!this.adsManager && !this._adsRequested) {
            await this._requestAds();
        }

        if (!this.adsManager) {
            // This happens for example if the VAST response was empty.
            // And this can happen if no ad was available (for this user/device)
            // And most often it happens if the VAST response did not yet return
            // Wait for a maximum time of _maxWaitTime before aborting

            this.debug('[_loadAds] Waiting for adsManager...');

            while (!this.adsManager && !this._adError && this._timeWaited < this._maxWaitTime) {
                // eslint-disable-next-line no-await-in-loop
                await this._delay(AD_READY_POLL_INTERVAL);

                this._timeWaited += AD_READY_POLL_INTERVAL;
            }

            this.debug(`[_loadAds] Waited ${this._timeWaited}ms`);

            // Log it
            A1GA.event({
                category: 'PrerollAudioAd',
                action: `[${this._adTagUsed}] loadAdsError`,
                label: `Waited (adsManager) (ms): ${this._timeWaited} - loaded: ${!!this.adsManager}`, // Note: This is the total time waited, not just for the adsManager
                nonInteraction: false, // Not sure if this is correct...
            });

            // if the adsManager still did not load, abort
            if (!this.adsManager) {
                this._adsRequested = false;

                this._adsRequestInProgress = null;

                // eslint-disable-next-line prefer-promise-reject-errors
                return Promise.reject('adsManager not loaded');
            }
        }

        try {
            // Note: Passing dummy values here. Not sure if this is correct or necessary...
            this.adsManager.init(640, 360, google.ima.ViewMode.NORMAL);

            // Before starting the ad, check if the user did not pause the player while loading
            if (this._requestedPlayerState === 'play') {
                this.adsManager.start();
            }

            return Promise.resolve('Starting ad');
        } catch (adError) {
            this.debug('[_loadAds] AdsManager could not be started', adError);

            // Log it
            A1GA.event({
                category: 'PrerollAudioAd',
                action: `[${this._adTagUsed}] adsManagerInitError`,
                label: `adError: ${adError}`, // Note: This is the total time waited, not just for the adsManager
                nonInteraction: false, // Not sure if this is correct...
            });

            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('adsManager could not be started', adError);
        }
    }

    enableDebugging = (enabled = true) => {
        this._debuggingEnabled = enabled;
    };

    debug = (...args) => {
        // eslint-disable-next-line no-param-reassign
        args[0] = `[AudioEngine] ${args[0]}`;

        // eslint-disable-next-line no-console
        this._debuggingEnabled && console.log(...args);
    };

    setSrc = (source) => {
        /* eslint-disable no-param-reassign */

        const url = new URL(window.location.href);

        if (url.searchParams.get('utm_source') === 'google_ad_campaign') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://www.antena1.com.br/stream/stream/1;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'ezSjeOoS') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp.crossradio.com.br/stream/1;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'jCbASe7X') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp02.crossradio.com.br:18000/stream/2;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'Hz4LIr52') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp02.crossradio.com.br:18000/stream/3;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'B2n5j3wN') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp02.crossradio.com.br:18000/stream/4;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'QgxROU83') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp02.crossradio.com.br:18000/stream/5;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'FxATU3vV') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp03.crossradio.com.br:19000/stream/1;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'lmdVbe1i') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp03.crossradio.com.br:19000/stream/2;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'iayEYwro') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp03.crossradio.com.br:19000/stream/3;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'IokfgVFl') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://ant1camp02.crossradio.com.br:18000/stream/1;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'qCQhBPIU') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp1;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'kYZXKK5e') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp2;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'sh6PDHpi') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp3;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'zoAyMhQl') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp4;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'TerZZg6P') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp5;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === '4bQgVmqh') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp6;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'mugzvp7k') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp7;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'tyOs79sH') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp8;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === '35opJywu') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp9;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'z2Nyg4sE') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp010;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'qLPjv0DH') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp011;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'FOxGd7Hw') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp012;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'KX1nQiAa') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp013;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === '9FT8MbnM') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp014;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === 'kRCKp20z') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp015;',
            }));

            source = adStream;
        } else if (url.searchParams.get('utm_source') === '8l30ou16') {
            const adStream = source.map((objeto) => ({
                ...objeto,
                src: 'https://antena1campanha.crossradio.com.br:29090/camp016;',
            }));

            source = adStream;
        }

        /* eslint-disable no-param-reassign */

        this._initIfNeeded();

        if (!this.unlocked) {
            return this._enqueuePostUnlockFunction(this.setSrc, [source]);
        }

        const pickedSrc = this._pickSource(source);

        this.debug('[setSource] source picked: ', pickedSrc);

        this.audio.src = pickedSrc;

        return true;
    };

    pauseAndStopBuffering = (timeToStop = 5000) => {
        if (this.isInitialized()) {
            this.debug('pauseAndStopBuffering...');

            this._requestedPlayerState = 'pause';

            if (this.adsManager && this._isAdPlaying) {
                this.adsManager.pause();
            }

            this.audio.pause();

            this._stopLiveStreamTimer = setTimeout(() => {
                this.debug('[pauseAndStopBuffering] Stopping buffering of live stream...');

                this._oldSrc = this.audio.src;

                this._oldVolume = this.audio.volume;

                this._stopLiveStreamTimer = null;

                this._clearEventListeners();

                this.audio.src = '';

                this.audio.load();

                if (this.audio.parentNode) {
                    this.audio.parentNode.removeChild(this.audio);
                }

                this.audio = null;

                if ('ontouchstart' in window && !this.unlocked) {
                    this.debug('[pauseAndStopBuffering] unlock listeners registered');

                    document.addEventListener('touchstart', this._unlock, true);

                    document.addEventListener('touchend', this._unlock, true);

                    // on some very few browsers this returns true, without ever firing any touch event :( (check https://stackoverflow.com/questions/46917481/ontouchstart-exists-in-window-object-on-non-touch-devices)
                    // so we add a normal click event listener as well that will also unlock the player, if it is fired. (This should not cause problems on mobile devices)
                    document.addEventListener('click', this._unlock, true);
                }
            }, timeToStop);
        }
    };

    play = () => {
        this._initIfNeeded();

        if (!this.unlocked) {
            return this._enqueuePostUnlockFunction(this.play);
        }

        this.debug('play');

        this._requestedPlayerState = 'play';

        if (this._stopLiveStreamTimer) {
            this.debug('[play] Cancelling timer that stops buffering of live stream');

            clearTimeout(this._stopLiveStreamTimer);

            this._stopLiveStreamTimer = null;
        }

        if (this._skipNextPrerollAd) {
            this.debug('[play] skipping next preroll ad');

            this._skipNextPrerollAd = false;

            // catch (not ignore): the media element's 'error' event already handles user-facing
            // notification for a failed source (see MediaPlayerComponent's _onError) - this just
            // avoids an "Uncaught (in promise)" console error for the same failure.
            this.audio.play().catch((error) => this.debug('[play] audio.play() rejected: ', error));

            return true;
        }

        // Try to load and play a preroll ad
        // If sucessfull, the player will start once the ad finished playing
        // In case of a problem or no ad, start the player manually
        // This might be due to the ad stuff not yet initialized, no ad, or any other error
        this._loadAds()
            .then()
            .catch((error) => {
                this.debug('[play] _loadAds error: ', error);

                this.audio.play().catch((playError) => this.debug('[play] audio.play() rejected: ', playError));
            });

        return true;
    };

    pause = () => {
        this._initIfNeeded();

        if (!this.unlocked) {
            this._enqueuePostUnlockFunction(this.pause);
        }

        this.debug('pause');

        this._requestedPlayerState = 'pause';

        if (this.adsManager && this._isAdPlaying) {
            // Note: I am not sure if we actually should be pausing the audio element as well, when an ad is playing.
            // But it does not seem to cause problems
            this.adsManager.pause();
        }

        return this.audio.pause();
    };

    load = () => {
        this._initIfNeeded();

        if (!this.unlocked) {
            this._enqueuePostUnlockFunction(this.load);
        }

        this.debug('load');

        return this.audio.load();
    };

    addEventListener = (type, cb, opts) => {
        this._initIfNeeded();

        if (!this.unlocked) {
            return this._enqueuePostUnlockFunction(this.addEventListener, [type, cb, opts]);
        }

        this.debug('[addEventListener] adding event listener', { type, cb, opts });

        if (!this._eventListeners) {
            this._eventListeners = [];
        }

        this._eventListeners.push({
            type,
            cb,
            opts,
        });

        return this.audio.addEventListener(type, cb, opts);
    };

    addAdEventListener = (type, cb, opts) => {
        this._initIfNeeded();

        if (!this.adsManager) {
            return this._enqueuePostAdsManagerLoadedFunction(this.addAdEventListener, [type, cb, opts]);
        }

        this.debug('[addAdEventListener] adding ad event listener', { type, cb, opts });

        if (!this._eventListeners) {
            this._eventListeners = [];
        }

        this._eventListeners.push({
            type,
            cb,
            opts,
        });

        return this.adsManager.addEventListener(type, cb, opts);
    };

    removeEventListener = (type, cb) => {
        this._initIfNeeded();

        if (!this.unlocked) {
            this._enqueuePostUnlockFunction(this.removeEventListener, [type, cb]);
        }

        this.debug('[removeEventListener] removing event listener', { type, cb });

        this.audio.removeEventListener(type, cb);

        this._eventListeners = this._eventListeners.filter((e) => e.cb !== cb);
    };

    _clearEventListeners = () => {
        if (this.audio && this._eventListeners) {
            this._eventListeners.forEach((e) => {
                this.audio.removeEventListener(e.type, e.cb);
            });
        }
    };

    _restoreEventListeners = () => {
        if (this._eventListeners) {
            this._eventListeners.forEach((e) => {
                this.audio.addEventListener(e.type, e.cb, e.opts);
            });
        }
    };

    setVolume = (volume) => {
        this._initIfNeeded();

        if (!this.unlocked) {
            return this._enqueuePostUnlockFunction(this.setVolume, [volume]);
        }

        this.audio.volume = volume;

        return this._setAdVolume(volume);
    };

    _setAdVolume = (volume) => {
        if (!this.adsManager) {
            return this._enqueuePostAdsManagerLoadedFunction(this._setAdVolume, [volume]);
        }

        return this.adsManager.setVolume(volume);
    }

    getAudio = () => {
        this._initIfNeeded();

        if (!this.unlocked) {
            return this._enqueuePostUnlockFunction(this.getAudio, []);
        }

        return this.audio;
    };

    isLiveStream = () => this.audio && this.audio.duration === Infinity;

    isInitialized = () => this.audio && this.unlocked;

    setInitListener = (listener) => {
        this._initListener = listener;
    };

    adIsPlaying = () => this.adsManager && this._isAdPlaying;

    setUserIdOrBrowserHash = (userIdOrBrowserHash) => {
        this._userIdOrBrowserHash = userIdOrBrowserHash;
    }

    setIsPro = (isPro) => {
        this._isPro = isPro;
    }

    addAdStuffLoadingEventListener = (cb) => {
        this._adStuffLoadingEventCallBack = cb;
    }

    setAdTagUrls = (adTagUrls) => {
        this._adTagUrls = adTagUrls;
    }

    skipNextPrerollAd = () => {
        this._skipNextPrerollAd = true;
    }
}

export default new AudioEngine();
